import {
  matchLeadingGreetingHead,
  normalizeNamePlaceholders,
  normalizeScriptWithPlaceholder,
  parseLeadingGreetingBoundary,
  stripSignOff,
} from './emailPreview';
import {
  CAMPAIGN_BODY_FIELD_DETAIL_PATTERN,
  CAMPAIGN_WEAK_GREETING_BODY_PROSE_PATTERN,
  isCampaignGreetingWithoutMessage,
  isCampaignSignOffLine,
  looksLikeCampaignBusinessIdentity,
  looksLikeCampaignIdentityDetail,
  looksLikeCampaignPersonIdentity,
  looksLikeCampaignSingleIdentity,
} from './campaignIdentityLines';

export const CAMPAIGN_GREETING_SKELETON = 'Hi [NAME],\n\n';

export function getCampaignMessageBodyCaretOffset(script) {
  if (typeof script !== 'string') return 0;

  const tokenIndex = script.indexOf('[NAME]');
  if (tokenIndex < 0) return script.length;

  const tokenEnd = tokenIndex + '[NAME]'.length;
  const bodySeparator = script.slice(tokenEnd).match(/^[,!?.:;]*[ \t]*(?:\r?\n[ \t]*){2}/);
  return tokenEnd + (bodySeparator?.[0].length || 0);
}

export function hasCampaignMessageBody(script, options = {}) {
  if (typeof script !== 'string' || !script.trim()) return false;

  const boundary = parseLeadingGreetingBoundary(script);
  let body = script;
  if (boundary.kind === 'recognized') {
    body = stripSignOff(script.slice(boundary.bodyStart), options);
  } else if (boundary.kind === 'ambiguous' && !boundary.isWeak) {
    body = script.slice(boundary.preserveStart);
  }

  const withoutTokens = normalizeNamePlaceholders(body).replace(/\[NAME\]/gi, '');
  return /[\p{L}\p{N}]/u.test(withoutTokens);
}

export function selectUploadedCampaignScripts({
  emailScripts,
  criteria,
  secondaryCriteria,
} = {}) {
  const uploadedScripts = Array.isArray(emailScripts) ? emailScripts : [];
  if (uploadedScripts.some(script => typeof script === 'string' && script.trim())) {
    return uploadedScripts;
  }

  const legacyScripts = [criteria, secondaryCriteria]
    .map(script => typeof script === 'string' ? script : '');
  return legacyScripts.some(script => script.trim()) ? legacyScripts : [];
}

export function findInvalidRequiredCampaignScriptIndexes(
  scripts,
  { sendMode = 'separate', neededScriptCount = 1, bodyFieldLabels = [] } = {}
) {
  const normalizedScripts = Array.isArray(scripts) ? scripts : [];
  const parsedScriptCount = Number(neededScriptCount);
  const requiredScriptCount = sendMode === 'combined'
    ? 1
    : Math.max(1, Number.isFinite(parsedScriptCount) ? Math.floor(parsedScriptCount) : 1);

  return Array.from({ length: requiredScriptCount }, (_, index) => index)
    .filter(index => !hasCampaignMessageBody(
      normalizedScripts[index],
      { bodyFieldLabels }
    ));
}

function normalizeCampaignScriptSlot(script, options = {}) {
  return typeof script === 'string' && script.trim()
    ? normalizeScriptWithPlaceholder(script, options)
    : '';
}

export function initializeCampaignScripts(uploadedScripts, options = {}) {
  const scripts = Array.isArray(uploadedScripts) ? uploadedScripts : [];
  const hasUploadedScript = scripts.some(
    script => typeof script === 'string' && script.trim()
  );

  const normalizedScripts = scripts.map(script => normalizeCampaignScriptSlot(script, options));

  return hasUploadedScript
    ? normalizedScripts
    : [CAMPAIGN_GREETING_SKELETON];
}

export function normalizeCampaignScriptsForLaunch(scripts, options = {}) {
  return (Array.isArray(scripts) ? scripts : [])
    .map(script => normalizeCampaignScriptSlot(script, options));
}

/**
 * Remove an advisor's own signature from the END of an uploaded campaign body.
 *
 * SiteSift appends the advisor's configured signature at send, so an uploaded
 * Script sheet that already ends in one would deliver two. That is a defect the
 * product can fix without asking, so it is stripped rather than refused. The
 * refusal in validateCampaignLaunchDraft stays for identity material that sits
 * MID-body, where removing it would change what the message says - this
 * function only ever cuts a block that runs to the end of the text, and only
 * when a real message body survives the cut.
 */
// "-- " is the RFC-3676 signature delimiter Outlook and Gmail emit; "___" and a
// rule of dashes are the same convention by hand. Any of them introduces the
// block below it exactly as a sign-off does.
const CAMPAIGN_SIGNATURE_DELIMITER_PATTERN = /^[-_=~*\u2014\u2013]{2,}$/u;

function introducesCampaignSignature(line) {
  const text = String(line || '').trim();
  return isCampaignSignOffLine(text) || CAMPAIGN_SIGNATURE_DELIMITER_PATTERN.test(text);
}

export function stripTrailingCampaignSignature(value, bodyFieldLabels = []) {
  const raw = String(value ?? '');
  if (!raw.trim()) return raw;

  const lines = raw.replace(/\r\n?/gu, '\n').split('\n');
  let end = lines.length;
  while (end > 0 && !lines[end - 1].trim()) end -= 1;
  if (end === 0) return raw;

  let blockStart = end;
  while (blockStart > 0 && lines[blockStart - 1].trim()) blockStart -= 1;

  const block = lines.slice(blockStart, end).map(line => line.trim());
  if (block.length === 0 || block.length > 8) return raw;

  // "Best,\nTaylor Brook" and "--\nTaylor Brook" both put the introducer inside
  // the same block as the name.
  let identityStart = 0;
  while (identityStart < block.length && introducesCampaignSignature(block[identityStart])) {
    identityStart += 1;
  }
  const identityBlock = block.slice(identityStart);
  const hasInlineSignOff = identityStart > 0;
  if (identityBlock.length === 0) return raw;

  const [firstLine, ...supportingLines] = identityBlock;
  const firstLineIsPerson = looksLikeCampaignPersonIdentity(firstLine, bodyFieldLabels)
    || looksLikeCampaignSingleIdentity(firstLine, bodyFieldLabels);
  const firstLineIsBusiness = !CAMPAIGN_BODY_FIELD_DETAIL_PATTERN.test(firstLine)
    && looksLikeCampaignBusinessIdentity(firstLine, bodyFieldLabels);
  if (!firstLineIsPerson && !firstLineIsBusiness) return raw;

  const supportingLinesAreIdentity = supportingLines.every(line => (
    looksLikeCampaignPersonIdentity(line, bodyFieldLabels)
    || looksLikeCampaignSingleIdentity(line, bodyFieldLabels)
    || looksLikeCampaignIdentityDetail(line)
  ));
  if (!supportingLinesAreIdentity) return raw;

  // Without a sign-off introducing it, a name needs supporting identity detail
  // before it can be read as a signature rather than as body copy.
  if (!hasInlineSignOff && !supportingLines.some(looksLikeCampaignIdentityDetail)) return raw;

  let cut = blockStart;
  // A sign-off or delimiter can stand a blank line above the block it
  // introduces; absorb it so the message does not end on a bare rule.
  for (;;) {
    let probe = cut;
    while (probe > 0 && !lines[probe - 1].trim()) probe -= 1;
    if (probe === 0 || !introducesCampaignSignature(lines[probe - 1])) break;
    cut = probe - 1;
  }

  const remaining = lines.slice(0, cut).join('\n').replace(/\s+$/u, '');
  if (!hasCampaignMessageBody(remaining, { bodyFieldLabels })) return raw;
  return remaining;
}

// An addressee token: a capitalized word, no digits, no interior spaces.
const UPLOADED_ADDRESSEE_TOKEN_PATTERN = /^[\p{Lu}\p{Lo}][\p{L}\p{M}'\u2019.-]*$/u;
// Words that are capitalized at the head of a sentence far more often than they
// name a recipient. Reading one of these as an addressee would delete body copy.
const UPLOADED_NON_ADDRESSEE_TOKEN_PATTERN = new RegExp(
  '^(?:thanks?|thank|best|regards|cheers|sincerely|greetings|hello|hi|hey|dear'
  + '|good|morning|afternoon|evening|please|could|can|would|will|should|we|i'
  + '|our|your|the|this|that|it|there|all|team|everyone|folks|sir|madam)$',
  'i'
);
// An addressee takes an addressee's punctuation - a comma or a colon, never a
// full stop or an exclamation mark, which belong to a sentence - and it stands
// alone on the greeting line. "Hi, Happy Friday! Is the space still available?"
// and "Hello Springfield, IL industrial space is what my client needs." both
// open with a Title-Case phrase that is the advisor's first sentence, not a
// recipient, and deleting it would send a message they never wrote.
const UPLOADED_ADDRESSEE_LINE_PATTERN = /^([^\r\n,:]{1,80})([,:]+[\t ]*)(\r?\n|$)/u;
const UPLOADED_ADDRESSEE_INLINE_PATTERN = /^([^\r\n,:]{1,80})([,:][\t ]+)(?=\S)/u;

function normalizeUploadedContactKey(value) {
  return String(value || '').replace(/\s+/gu, ' ').trim().toLocaleLowerCase();
}

// The workbook knows who the recipients are. Their names - and their first
// names - are the only evidence that needs no guess about capitalisation, so
// they decide the inline case.
function buildUploadedContactKeys(contactNames) {
  const keys = new Set();
  (Array.isArray(contactNames) ? contactNames : []).forEach(name => {
    const full = normalizeUploadedContactKey(name);
    if (!full) return;
    keys.add(full);
    const [first] = full.split(' ');
    if (first) keys.add(first);
  });
  return keys;
}

function acceptsUploadedAddressee(candidate, contactKeys) {
  return contactKeys.has(normalizeUploadedContactKey(candidate))
    || looksLikeUploadedAddressee(candidate);
}

/**
 * An addressee that shares its line with the body.
 *
 * The product never guesses at names. The workbook is the only authority here:
 * a candidate is a recipient when it matches a contact's name or first name,
 * and otherwise it is the advisor's own copy, kept verbatim with the [NAME]
 * greeting prepended on its own line. "Happy Friday", "Great News" and
 * "Best Regards" are shaped exactly like names, so no capitalisation rule can
 * separate them from one - and deleting the advisor's first words is the worse
 * failure of the two.
 */
function acceptsInlineUploadedAddressee(candidate, contactKeys) {
  return contactKeys.has(normalizeUploadedContactKey(candidate));
}

function looksLikeUploadedAddressee(candidate) {
  const tokens = String(candidate || '').trim().split(/\s+/u).filter(Boolean);
  if (tokens.length === 0 || tokens.length > 4) return false;
  if (!tokens.every(token => UPLOADED_ADDRESSEE_TOKEN_PATTERN.test(token))) return false;
  return !tokens.some(token => UPLOADED_NON_ADDRESSEE_TOKEN_PATTERN.test(token));
}

/**
 * Give an UPLOADED script the [NAME] placeholder it needs to be personalized.
 *
 * The composer path stays lossless on purpose: text an advisor is typing must
 * never be rewritten under the caret. An uploaded Script sheet is different -
 * it is a finished email the advisor already wrote elsewhere, and asking them
 * to hand-edit a greeting they cannot see the need for is the defect. Body copy
 * is never dropped here: an addressee is removed only when it reads as one and
 * a message still follows it; otherwise the placeholder is added in front of
 * everything the upload contained.
 */
function personalizeUploadedGreeting(script, options = {}) {
  const raw = normalizeNamePlaceholders(script);
  if (/\[NAME\]/i.test(raw)) return normalizeScriptWithPlaceholder(raw, options);

  // A ONE-LINE message whose whole content is a greeting and an addressee
  // ("Hi Thanks.") is what the launch gate refuses, and no placeholder can
  // repair it, so leave it exactly as uploaded and let that refusal fire. A
  // greeting, a blank line and one short question is the opposite case - the
  // shortest real outreach email there is - and must be personalized.
  if (!/\r?\n/u.test(raw) && isCampaignGreetingWithoutMessage(raw)) return raw;

  const boundary = parseLeadingGreetingBoundary(raw);
  if (boundary.kind === 'recognized') return normalizeScriptWithPlaceholder(raw, options);

  const body = stripSignOff(raw, options);
  const head = matchLeadingGreetingHead(body);
  if (!head || CAMPAIGN_WEAK_GREETING_BODY_PROSE_PATTERN.test(body.trimStart())) {
    return `${CAMPAIGN_GREETING_SKELETON}${body.trim()}`;
  }

  const contactKeys = buildUploadedContactKeys(options.contactNames);
  const remainder = body.slice(head.restStart);

  const lineSegment = remainder.match(UPLOADED_ADDRESSEE_LINE_PATTERN);
  if (lineSegment && acceptsUploadedAddressee(lineSegment[1], contactKeys)) {
    return `${head.greeting} [NAME],\n\n${remainder.slice(lineSegment[0].length).trim()}`;
  }

  const inlineSegment = remainder.match(UPLOADED_ADDRESSEE_INLINE_PATTERN);
  if (inlineSegment && acceptsInlineUploadedAddressee(inlineSegment[1], contactKeys)) {
    return `${head.greeting} [NAME],\n\n${remainder.slice(inlineSegment[0].length).trim()}`;
  }

  return `${head.greeting} [NAME],\n\n${remainder.trim()}`;
}

/**
 * Normalize one uploaded Script sheet message: remove leading quote markers,
 * drop the trailing signature and attach the name placeholder. Typed copy is
 * normalized separately and keeps intentional quote markers.
 */
export function normalizeUploadedCampaignScript(script, options = {}) {
  if (typeof script !== 'string' || !script.trim()) return script;

  // Horizontal whitespace only: never consume the next line or a >= operator.
  const unquoted = script.replace(/^[ \t]*>+(?![=>])[ \t]*/gm, '');
  if (!unquoted.trim()) return unquoted;

  const withoutSignature = stripTrailingCampaignSignature(
    unquoted,
    options.bodyFieldLabels || []
  );
  return personalizeUploadedGreeting(withoutSignature, options);
}

export function prepareUploadedCampaignScripts(uploadedScripts, options = {}) {
  const scripts = Array.isArray(uploadedScripts) ? uploadedScripts : [];
  const hasUploadedScript = scripts.some(
    script => typeof script === 'string' && script.trim()
  );
  if (!hasUploadedScript) return [CAMPAIGN_GREETING_SKELETON];

  return scripts.map(script => (
    typeof script === 'string' && script.trim()
      ? normalizeUploadedCampaignScript(script, options)
      : ''
  ));
}
